type ZendeskTokenCallback = (jwt?: string) => void;

export type ZendeskLoginHandler = (callback: ZendeskTokenCallback) => void;

/** null means the login succeeded; anything else is Zendesk's LoginFailedError. */
export type ZendeskLoginResultHandler = (error: unknown) => void;

export interface ZendeskApi {
  (widget: 'messenger', action: 'open' | 'close' | 'show' | 'hide'): void;
  (
    widget: 'messenger',
    action: 'loginUser',
    handler: ZendeskLoginHandler,
    onResult?: ZendeskLoginResultHandler,
  ): void;
  (widget: 'messenger', action: 'logoutUser'): void;
  (widget: 'messenger', action: 'resetWidget', done?: () => void): void;
}

export interface ZendeskPublicApi {
  open: () => Promise<void>;
  isLoaded: () => boolean;
}

declare global {
  interface Window {
    zE?: ZendeskApi;
    __pbZendesk?: {
      key: string | null;
      authEnabled?: boolean;
      name?: string;
    };
    paybisChat?: ZendeskPublicApi;
  }
}

export const isZendeskLoaded = (): boolean => window.zE !== undefined;

const MARKER_KEY = 'pb.zendesk.identity';

export interface ZendeskIdentity {
  sub: string;
  key: string;
}

const isIdentity = (value: unknown): value is ZendeskIdentity =>
  typeof value === 'object'
  && value !== null
  && typeof (value as ZendeskIdentity).sub === 'string'
  && typeof (value as ZendeskIdentity).key === 'string';

export const readIdentityMarker = (): ZendeskIdentity | null => {
  try {
    const raw = window.localStorage.getItem(MARKER_KEY);
    if (raw === null) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);

    return isIdentity(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const writeIdentityMarker = (identity: ZendeskIdentity): void => {
  try {
    window.localStorage.setItem(MARKER_KEY, JSON.stringify(identity));
  } catch {
    // Storage unavailable — reconciliation degrades to "always anonymous",
    // which is safe.
  }
};

export const clearIdentityMarker = (): void => {
  try {
    window.localStorage.removeItem(MARKER_KEY);
  } catch {
    // As above.
  }
};

const LOGOUT_PATH = '/user/logout';

export const isLogoutForm = (form: HTMLFormElement): boolean => {
  const action = form.getAttribute('action');
  if (action === null || action === '') {
    return false;
  }

  try {
    const { pathname } = new URL(action, window.location.origin);
    const normalized = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;

    return normalized.endsWith(LOGOUT_PATH);
  } catch {
    return false;
  }
};
